import { useQuery } from '@tanstack/react-query'
import { providerService, type ModelConfigEntry } from '@/services/provider.service'
import { isEnabledModel } from '@/services/enabled-models'
import { resolveModelCapabilities } from '@/services/model-capabilities'
import { modelBindingService, type GetModelDefaultsResponse, type ModelDefault, type ModelDefaultPurpose } from '@/services/model-binding.service'
import { resourceService, type ModelLifecycleState, type ModelModality, type ModelResource, type UnifiedResource, type Resource as BackendResource } from '@/services/resource.service'
import { normalizeLifecycleState } from '@/lib/model-lifecycle'
import { githubIntegrationsService, type GitHubInstallation } from '@/shared/api/github-integrations.service'
import { resolveModelModality } from '@/pages/governance/model-modality'
import { listTools } from '@/services/tool.service'
import type { ToolListResponse } from '@/types/tool.types'
import { agentHostService, type AgentHostListResponse } from '@/services/agentHostService'
import { modelAvailabilityService, type ModelAvailabilityAlertsResponse } from '@/services/model-availability.service'
import { queryPolicies } from '@/shared/api/query-policies'
import { parseInvocationUsage } from '@/shared/api/invocation-usage'
import { servedCredentialSites } from '@/services/connectionNesting'
import { resourceKeys } from './useInvalidateResourceCache'

export { resourceKeys, useInvalidateResourceCache } from './useInvalidateResourceCache'

/**
 * Fetch models from all active providers via the bulk endpoint.
 * Maps the backend response to ModelResource[] for the UI.
 */
async function fetchAllModels(): Promise<ModelResource[]> {
  // Fetch providers list and bulk models in parallel
  const [providersResponse, bulkResponse] = await Promise.all([
    providerService.listProviders({ status: 'active' }),
    providerService.listAllModels(),
  ])

  const providers = providersResponse.providers || []

  // Build a map of provider ID -> provider info
  const providerMap = new Map<string, {
    enabledModels: string[]
    modelConfigurations?: Record<string, ModelConfigEntry>
    name: string
    providerType: string
    baseUrl?: string
    createdAt?: string
    updatedAt?: string
  }>()

  for (const p of providers) {
    providerMap.set(p.id, {
      enabledModels: p.enabledModels || [],
      modelConfigurations: p.modelConfigurations,
      name: p.name,
      providerType: p.providerType,
      baseUrl: p.baseUrl,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
    })
  }

  return mapBulkResponse(bulkResponse, providerMap)
}

/**
 * Map the bulk endpoint response to ModelResource[].
 */
function mapBulkResponse(
  bulkResponse: Awaited<ReturnType<typeof providerService.listAllModels>>,
  providerMap: Map<string, { enabledModels: string[]; modelConfigurations?: Record<string, ModelConfigEntry>; name: string; providerType: string; baseUrl?: string; createdAt?: string; updatedAt?: string }>,
): ModelResource[] {
  const allModels: ModelResource[] = []

  for (const providerResult of bulkResponse.providers || []) {
    const providerInfo = providerMap.get(providerResult.providerId)
    const enabledModels = providerInfo?.enabledModels || []
    const providerName = providerInfo?.name || providerResult.providerName
    const providerType = providerInfo?.providerType || providerResult.providerType

    if (providerResult.error) {
      for (const modelId of enabledModels) {
        allModels.push({
          uri: `${providerResult.providerId}:${modelId}`,
          modelId,
          modelVersion: configuredAzureModelVersion(providerInfo, modelId),
          name: modelId,
          provider: providerName,
          modelFamily: providerType,
          modality: 'chat',
          contextSize: 0,
          capabilities: ['chat'],
          pricing: { inputTokens: 0, outputTokens: 0, currency: 'USD' },
          status: 'active',
          lifecycleState: 'active',
          isConfigured: true,
          endpoint: providerResult.baseUrl,
          createdAt: providerInfo?.createdAt || '',
          updatedAt: providerInfo?.updatedAt || '',
        })
      }
      continue
    }

    mapProviderModels(
      providerResult.providerId,
      providerResult.models || [],
      enabledModels,
      providerName,
      providerType,
      providerResult.baseUrl || providerInfo?.baseUrl,
      providerInfo,
      allModels,
    )
  }

  return allModels
}

/**
 * Shared logic: filter + map provider models to ModelResource[].
 */
export function mapProviderModels(
  providerId: string,
  models: any[],
  enabledModels: string[],
  providerName: string,
  providerType: string,
  endpoint: string | undefined,
  providerInfo: { createdAt?: string; updatedAt?: string; modelConfigurations?: Record<string, ModelConfigEntry> } | undefined,
  out: ModelResource[],
) {
  // Contract (empty list = no models, never a wildcard): see services/enabled-models.ts.
  const matchesEnabled = (modelId: string) => isEnabledModel(enabledModels, modelId)

  for (const model of models) {
    const modelId = model.id || model.modelIdentifier || ''
    if (!matchesEnabled(modelId)) continue

    const modality = resolveModelModality(
      model.primaryModality || model.primary_modality,
      model.modality
    ) || 'chat'
    const capabilities = resolveModelCapabilities(model, { modality })

    const lifecycleState = normalizeLifecycleState(model.lifecycleState || model.lifecycle_state)
    const configuredModelVersion = configuredAzureModelVersion(providerInfo, modelId)
    out.push({
      uri: `${providerId}:${modelId}`,
      modelId,
      modelFormat: optionalModelCoordinate(model.format ?? model.modelFormat),
      modelVersion: configuredModelVersion ?? optionalModelCoordinate(model.catalogVersion ?? model.modelVersion),
      name: model.displayName || model.display_name || model.name || modelId,
      provider: providerName,
      modelFamily: providerType,
      modality,
      displayModality: model.displayModality || model.display_modality,
      contextSize: model.contextWindow || model.context_window || 0,
      capabilities,
      pricing: {
        inputTokens: model.inputTokenPrice || model.input_token_price || 0,
        outputTokens: model.outputTokenPrice || model.output_token_price || 0,
        currency: 'USD',
      },
      status: lifecycleState === 'active' && model.status !== 'deprecated' ? 'active' : 'inactive',
      lifecycleState,
      isConfigured: true,
      isAvailable: model.isAvailable ?? model.is_available,
      isDeprecated: model.isDeprecated ?? model.is_deprecated,
      endpoint,
      createdAt: model.createdAt || providerInfo?.createdAt || '',
      updatedAt: model.updatedAt || providerInfo?.updatedAt || '',
    })
  }
}

function configuredAzureModelVersion(
  providerInfo: { modelConfigurations?: Record<string, ModelConfigEntry> } | undefined,
  modelId: string,
): string | undefined {
  return optionalModelCoordinate(providerInfo?.modelConfigurations?.[modelId]?.azure?.modelVersion)
}

function optionalModelCoordinate(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

// Persisted defaults can conflict with a provider's current explicit selection.
// Preserve such rows for remediation using only data returned by defaults API.
export function mergePersistedDefaultModels(
  models: ModelResource[],
  defaultsResponse: { defaults?: Partial<Record<ModelDefaultPurpose, ModelDefault | null>> } | undefined,
): ModelResource[] {
  const result = [...models]
  const indexByURI = new Map(result.map((model, index) => [model.uri, index]))

  for (const defaultEntry of Object.values(defaultsResponse?.defaults ?? {})) {
    if (!defaultEntry) continue

    const lifecycleState = normalizeLifecycleState(defaultEntry.lifecycleState)
    const isConfigured = defaultEntry.isConfigured ?? true
    const status = isDefaultSelectable(defaultEntry, lifecycleState, isConfigured) ? 'active' : 'inactive'
    const existingIndex = indexByURI.get(defaultEntry.providerModelUri)

    if (existingIndex !== undefined) {
      const existing = result[existingIndex]
      if (!existing) continue
      result[existingIndex] = {
        ...existing,
        modelId: defaultEntry.modelId,
        status,
        lifecycleState,
        isConfigured,
        isAvailable: defaultEntry.isAvailable,
        isDeprecated: defaultEntry.isDeprecated,
      }
      continue
    }

    const appendedIndex = result.length
    result.push({
      uri: defaultEntry.providerModelUri,
      modelId: defaultEntry.modelId,
      name: defaultEntry.modelDisplayName || defaultEntry.modelId,
      provider: defaultEntry.providerName,
      modelFamily: defaultEntry.providerType,
      modality: defaultPurposeToModality(defaultEntry.purpose),
      contextSize: 0,
      capabilities: [],
      pricing: { inputTokens: 0, outputTokens: 0, currency: 'USD' },
      status,
      lifecycleState,
      isConfigured,
      isAvailable: defaultEntry.isAvailable,
      isDeprecated: defaultEntry.isDeprecated,
      createdAt: '',
      updatedAt: '',
    })
    indexByURI.set(defaultEntry.providerModelUri, appendedIndex)
  }

  return result
}

function isDefaultSelectable(defaultEntry: ModelDefault, lifecycleState: ModelLifecycleState, isConfigured: boolean): boolean {
  return isConfigured && lifecycleState === 'active' && defaultEntry.isAvailable !== false && defaultEntry.isDeprecated !== true
}

function defaultPurposeToModality(purpose: ModelDefaultPurpose): ModelModality {
  switch (purpose) {
    case 'code':
    case 'translation':
    case 'data_enrichment':
      return 'chat'
    default:
      return purpose
  }
}

/**
 * Hook for fetching all models from all active providers.
 * Uses the bulk endpoint GET /v1/providers/models for a single API call.
 */
export function useModels(enabled = true) {
  return useQuery<ModelResource[], Error>({
    ...queryPolicies.catalog,
    // A provider save can invalidate this list while its page is unmounted.
    refetchOnMount: (query) => query.state.isInvalidated,
    queryKey: resourceKeys.models,
    queryFn: fetchAllModels,
    enabled,
  })
}

/**
 * Hook for fetching model defaults (which model is default for each purpose).
 */
export function useModelDefaults(enabled = true) {
  return useQuery<GetModelDefaultsResponse, Error>({
    ...queryPolicies.operational,
    queryKey: resourceKeys.modelDefaults,
    queryFn: () => modelBindingService.getModelDefaults(),
    enabled,
  })
}

/**
 * Unresolved scanner findings are kept separately from model catalog data so
 * remediation writes can refetch both sources without treating the UI as the
 * authority that clears an alert.
 */
export function useModelAvailabilityAlerts(enabled = true) {
  return useQuery<ModelAvailabilityAlertsResponse, Error>({
    ...queryPolicies.operational,
    queryKey: resourceKeys.modelAvailabilityAlerts,
    queryFn: () => modelAvailabilityService.getAlerts(false),
    enabled,
  })
}

/**
 * Hook for fetching connection resources and GitHub installations.
 */
export function useConnectionResources(enabled = true) {
  return useQuery<{ resources: UnifiedResource[]; githubInstallations: GitHubInstallation[] }, Error>({
    ...queryPolicies.catalog,
    queryKey: resourceKeys.connections,
    queryFn: async () => {
      const [resourcesResult, githubResult] = await Promise.allSettled([
        resourceService.listResources(),
        githubIntegrationsService.listInstallations(),
      ])

      const resources: UnifiedResource[] = []
      if (resourcesResult.status === 'fulfilled') {
        const response = resourcesResult.value
        const resourcesList = Array.isArray(response)
          ? response
          : (response as any)?.resources || []
        // Deduplicate by name+type (temporary safety net until V88 UNIQUE constraint is applied)
        const seen = new Set<string>()
        for (const r of resourcesList) {
          const unified = convertToUnifiedResource(r)
          const resourceType = unified.metadata?.category || unified.metadata?.resourceSubtype || ''
          const dedupeKey = `${unified.name}::${resourceType}`
          if (seen.has(dedupeKey)) continue
          seen.add(dedupeKey)
          resources.push(unified)
        }
      }

      const githubInstallations: GitHubInstallation[] = []
      if (githubResult.status === 'fulfilled') {
        const installations = githubResult.value.installations || []
        githubInstallations.push(...installations)
      }

      return { resources, githubInstallations }
    },
    enabled,
  })
}

/**
 * Hook for fetching all tools.
 */
export function useTools(enabled = true) {
  return useQuery<ToolListResponse, Error>({
    ...queryPolicies.catalog,
    queryKey: resourceKeys.tools,
    queryFn: () => listTools({ limit: 200 }),
    enabled,
  })
}

/**
 * Hook for fetching all agent hosts (used to resolve host IDs to names).
 */
export function useAgentHosts(enabled = true) {
  return useQuery<AgentHostListResponse, Error>({
    ...queryPolicies.catalog,
    queryKey: resourceKeys.agentHosts,
    queryFn: () => agentHostService.listHosts(),
    enabled,
  })
}

/**
 * Hook for every configured provider used by the Model Provider Hub.
 *
 * Inactive and failed providers must remain visible here so an administrator
 * can diagnose and recover them. Runtime model discovery continues to use
 * active-only provider lists through fetchAllModels.
 * Cached so navigating from the hub → detail → back doesn't trigger a
 * re-fetch every time. Invalidate via useInvalidateResourceCache after
 * any mutation (save / delete / connect new).
 */
export function useConfiguredProviders(enabled = true) {
  return useQuery({
    ...queryPolicies.operational,
    queryKey: resourceKeys.providers,
    queryFn: () => providerService.listProviders(),
    enabled,
  })
}

/**
 * Hook for a single provider. The detail page edits a specific row, so
 * keying by id keeps the cache scoped per row — switching between two
 * OpenRouter accounts hits the cache for each, not a shared bucket.
 */
export function useProvider(providerId?: string) {
  return useQuery({
    ...queryPolicies.operational,
    queryKey: providerId ? resourceKeys.providerById(providerId) : ['resources', 'provider', '__none__'],
    queryFn: () => providerService.getProvider(providerId!),
    enabled: !!providerId,
  })
}

// Helper function to convert backend Resource to UnifiedResource
// Exported as _testOnly for unit testing (not part of public API).
export function convertToUnifiedResource_testOnly(backendResource: BackendResource): UnifiedResource {
  return convertToUnifiedResource(backendResource)
}
const SERVED_CONNECTION_STATUSES = new Set(['active', 'pending', 'error'])

/**
 * Backend lifecycle word for the next Connections table: active, pending and
 * error pass through, every other stored value (inactive, disabled,
 * deprecated, missing) reads as inactive.
 */
function servedConnectionFacts(backendResource: BackendResource): NonNullable<UnifiedResource['served']> {
  const raw = String(backendResource.status ?? '').toLowerCase()
  const status = (SERVED_CONNECTION_STATUSES.has(raw) ? raw : 'inactive') as NonNullable<UnifiedResource['served']>['status']
  const tags = Array.isArray(backendResource.tags)
    ? backendResource.tags.filter((tag): tag is string => typeof tag === 'string' && tag.trim() !== '')
    : []
  return { status, tags }
}

/**
 * Access as governance serves it (read, write), used exactly as served. When
 * none is served (a governance build without the field, or a kind that gets
 * none) the owner default is Read only, never Write; the type is never used to
 * infer access (Agion#2253).
 */
function servedCapabilities(served: Partial<BackendResource['capabilities']> | undefined): UnifiedResource['metadata']['capabilities'] {
  if (!served) {
    return { read: true, write: false, execute: false, stream: false }
  }
  return {
    read: served.read === true,
    write: served.write === true,
    execute: false,
    stream: false,
  }
}

export function convertToUnifiedResource(backendResource: BackendResource): UnifiedResource {
  const resourceType = (backendResource as any).resourceType || backendResource.resource_type || 'unknown'
  const credentialId = (backendResource as any).credentialId || backendResource.credential_id
  const usageCount = (backendResource as any).usageCount || backendResource.usage_count || 0
  const createdAt = (backendResource as any).createdAt || backendResource.created_at
  const updatedAt = (backendResource as any).updatedAt || backendResource.updated_at

  const resourceData = (backendResource as any).resourceData || (backendResource as any).resource_data || {}
  const connectorType = resourceData.connector_type || resourceData.connectorType
  const resourceSubtype = connectorType || resourceType
  const credentialSites = servedCredentialSites(backendResource)

  return {
    ...(credentialSites ? { credentialSites } : {}),
    id: backendResource.id,
    usage: parseInvocationUsage((backendResource as BackendResource & { usage?: unknown }).usage),
    uri: `agion://resource/${backendResource.id}`,
    name: backendResource.name,
    type: 'resource',
    description: backendResource.description || resourceType,
    status: backendResource.status === 'active' ? 'active' : 'inactive',
    served: servedConnectionFacts(backendResource),
    metadata: {
      resourceSubtype: resourceSubtype as any,
      category: resourceType,
      endpoint: resourceData.baseUrl || resourceData.base_url ||
        resourceData.endpoint ||
        (resourceData.host && resourceData.port ? `${resourceData.host}:${resourceData.port}` : resourceData.host) ||
        undefined,
      capabilities: servedCapabilities(backendResource.capabilities),
      authentication: {
        type: credentialId ? 'api_key' : 'none',
        configured: !!credentialId,
      },
      schemas: resourceData.schema
        ? (resourceData.schema as string).split(',').map((s: string) => s.trim()).filter(Boolean)
        : undefined,
      usageCount: usageCount,
    },
    createdAt: createdAt,
    updatedAt: updatedAt,
  }
}
